<script setup lang="ts">
import type { HTMLAttributes, InputHTMLAttributes } from "vue";
import { computed, useTemplateRef } from "vue";

import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";

import BaseInputText from "./internal/BaseInputText.vue";

defineOptions({ inheritAttrs: false });

// vue-ignore so properties of InputHTMLAttributes are NOT omitted from $attrs
interface Props extends /* @vue-ignore */ InputHTMLAttributes {
  controlType: "text";
  iconBefore?: Icon;
  iconAfter?: Icon;
  label?: string;
  iconBeforeTitle?: string; // Screen readers will see this (or the title below) as the label for the input
  iconAfterTitle?: string; // (both work identically, and if both are provided both will be read)
  size?: "small" | "medium";
  disabled?: boolean;
  wrapperClass?: HTMLAttributes["class"];
}

defineProps<Props>();

const modelValue = defineModel<string>({ required: true });

const textInputRef = useTemplateRef("textInput");

// Get the value of the input (can be used eg. when parent updated on blur)
defineExpose({
  focus: () => void textInputRef.value?.focus(),
  blur: () => void textInputRef.value?.blur(),
  value: computed(() => modelValue.value),
});
</script>

<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <label class="base-input" :class="wrapperClass" :aria-label="label || ''">
    <div v-if="label" class="label">{{ label }}</div>
    <div class="input-area" :class="[size ?? 'medium', { disabled }]">
      <SvgIcon
        v-if="iconBefore"
        :name="iconBefore"
        class="icon"
        :title="iconBeforeTitle"
        :role="iconBeforeTitle ? 'img' : 'presentation'"
      />
      <BaseInputText
        v-if="controlType === 'text'"
        ref="textInput"
        v-model="modelValue"
        v-bind="$attrs"
        class="input-text"
        :disabled
      />
      <slot name="after" />
      <SvgIcon
        v-if="iconAfter"
        :name="iconAfter"
        class="icon"
        :title="iconAfterTitle"
        :role="iconAfterTitle ? 'img' : 'presentation'"
      />
    </div>
  </label>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";
@use "@/styles/font";

$height-small: 32px;
$height-medium: 36px;

.base-input {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    @include typography.small("semi-bold");

    cursor: pointer;
  }

  .input-area {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 8px;
    border-radius: 6px;
    font-size: 14px;
    color: colors.$text;
    background-color: colors.$background-ui;
    position: relative;

    // Use pseudo-element for border - only fix for Chrome autofill rendering bug
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border: 1px solid colors.$border-subtle;
      border-radius: inherit;
      pointer-events: none;
    }

    .icon {
      color: colors.$icon-subtle;
      flex-shrink: 0;
    }

    .input-text {
      min-width: 0;
      flex-grow: 1;
    }

    // Size: small
    &.small {
      @include typography.small("medium");

      height: $height-small;
      padding: 10px 8px;

      .icon {
        width: 16px;
        height: 16px;
      }
    }

    // Size: medium
    &.medium {
      height: $height-medium;
      padding: 10px 12px;
      font-weight: font.$weight-semi-bold;
      line-height: 18px;

      .icon {
        width: 20px;
        height: 20px;
      }
    }

    // Disabled
    &.disabled {
      background-color: colors.$background-disabled;
      color: colors.$text-placeholder;

      .icon {
        color: colors.$text-placeholder;
      }
    }

    &:not(.disabled) {
      cursor: pointer; // To match global input styling
    }

    // Focused
    &:focus-within::after {
      border-color: colors.$border-focused;
    }

    &:focus-within .icon {
      color: colors.$icon;
    }

    // Hover
    &:hover:not(:focus-within, .disabled) {
      &::after {
        border-color: colors.$border;
      }

      .icon {
        color: colors.$icon;
      }
    }
  }
}
</style>
